/**
 * `PlatformPrinter` on Electron — the Star **LAN** backend (PLAN §5.4, A6).
 *
 * This adapter presents the shell's `pc:star-print:*` channels with the same
 * shape `star-print.service.ts` already consumes on a phone, so the whole
 * printing stack above it — the central `printingService`, the bitmap
 * renderers, `star_printers_v2` persistence, readiness polling, auto-reconnect,
 * the troubleshoot dialog — runs on a desktop register unchanged.
 *
 * ## Why this file exists at all: hazard F2
 *
 * Under Model B, `@capacitor/core` has an explicit fallback: when a custom
 * platform is set and a plugin has no implementation for it, it loads the
 * **`web`** one (`node_modules/@capacitor/core/dist/index.js:79-86`). For
 * StarPrint that is `StarPrintWeb` — an in-memory mock that advertises
 * "Mock LAN mC-Print3" and resolves every print as a success. A register that
 * believes it printed a receipt it never printed is worse than one that cannot
 * print.
 *
 * **So the plugin is never imported on this path.** `createPlatformPrinter`
 * routes `star-electron-lan` here, this module imports only the bridge, and
 * nothing in the desktop graph reaches
 * `@am3nd-org/capacitor-starprint-plugin` — which is observable from a real
 * Electron window, because a plugin that is never imported never calls
 * `registerPlugin`, so `Capacitor.Plugins.StarPrint` stays `undefined`.
 * `tests/electron/f2-star-print-mock.spec.ts` asserts exactly that, alongside a
 * control that proves the web fallback really does fire on this runtime for a
 * plugin that *does* declare one.
 *
 * ## Fail closed, never silently
 *
 * Every method here goes through the allow-listed, schema-validated bridge and
 * surfaces the shell's own typed failure. With no printer configured the
 * operator gets a real error — never a mock printer, never a resolved print.
 */
import type { PluginListenerHandle } from '@capacitor/core';
import type {
  StarPrinter,
  StarPrintResult,
  StarPrinterStatus,
  StarPrinterTarget,
  StarPrintEventMap,
  StarPrintReceiptItem,
  StarPrinterConnection,
  StarPrintImageRequest,
  StarRawCommandRequest,
  StarPrintReceiptRequest,
  StarOpenCashDrawerRequest,
  BluetoothAuthorizationResult,
} from '@am3nd-org/capacitor-starprint-plugin';

import { starPrintBridge, unwrapBridgeResult } from './bridge';
import type {
  StarPrintBackend,
  StarDiscoverResult,
  StarDiscoverOptions,
  PrinterAvailability,
} from '../services/types';

/** Desktop is LAN only (Q8): no Bluetooth, BLE or USB transports. */
const ELECTRON_INTERFACES = ['lan'] as const;

type LanConnection = {
  interfaceType: 'lan';
  identifier?: string;
  model?: string;
  name?: string;
  ipAddress?: string;
  address?: string;
  port?: number;
  timeoutMs?: number;
};

type LanTarget = {
  connectionId?: string;
  identifier?: string;
  interfaceType?: 'lan';
  connection?: LanConnection;
  timeoutMs?: number;
};

/** Drops keys the bridge's `.strict()` schemas would reject. */
function toLanConnection(connection: StarPrinterConnection): LanConnection {
  if (connection.interfaceType !== 'lan') {
    throw new Error(
      `A desktop register prints over the network only, but a '${connection.interfaceType}' ` +
        'printer was selected. Add the printer by IP address instead.'
    );
  }
  return {
    interfaceType: 'lan',
    identifier: connection.identifier,
    model: connection.model,
    name: connection.name,
    ipAddress: connection.ipAddress,
    address: connection.address,
    port: connection.port,
    timeoutMs: connection.timeoutMs,
  };
}

function toLanTarget(target: StarPrinterTarget | undefined): LanTarget {
  if (!target) return {};
  return {
    connectionId: target.connectionId,
    identifier: target.identifier,
    interfaceType: target.interfaceType === 'lan' ? 'lan' : undefined,
    connection: target.connection ? toLanConnection(target.connection) : undefined,
    timeoutMs: target.timeoutMs,
  };
}

/**
 * The contract's receipt items are the plugin's, minus the transports desktop
 * does not have. Anything the LAN encoder refuses comes back as an explicit
 * `UNSUPPORTED` failure from main rather than being dropped here, so a receipt
 * never prints silently incomplete.
 */
function toLanItems(request: StarPrintReceiptRequest): StarPrintReceiptItem[] {
  if (request.items && request.items.length > 0) return request.items;
  // The legacy `lines` field predates `items`; it is still in the plugin's
  // request type, so it is normalised here rather than ignored.
  return request.lines ?? [];
}

function toBase64(request: StarRawCommandRequest): string {
  if (request.commandsBase64 !== undefined) return request.commandsBase64;
  const bytes = Uint8Array.from(request.commands ?? []);
  let binary = '';
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary);
}

/** The bridge's status event, projected onto the plugin's event shape. */
type StatusListener = (event: StarPrintEventMap['printerStatusChanged']) => void;
type DisconnectListener = (event: StarPrintEventMap['printerDisconnected']) => void;

export function createElectronStarPrintBackend(): StarPrintBackend {
  // Throws `ElectronBridgeUnavailableError` when the shell is absent, which is
  // the fail-closed answer: no bridge means no printing, never a mock.
  const bridge = starPrintBridge();

  const statusListeners = new Set<StatusListener>();
  const disconnectListeners = new Set<DisconnectListener>();
  let unsubscribe: (() => void) | null = null;
  let lastConnected = false;

  const ensureSubscribed = (): void => {
    if (unsubscribe !== null) return;
    unsubscribe = bridge.onStatus((event) => {
      const status = event.status as StarPrinterStatus;
      statusListeners.forEach((listener) =>
        listener({ connectionId: event.connectionId, status })
      );
      // The plugin emits a distinct `printerDisconnected`; the LAN backend
      // expresses the same fact as `connected: false`, so the edge is
      // translated here and `star-print.service.ts` keeps its existing
      // connection bookkeeping without a desktop branch.
      if (lastConnected && !status.connected) {
        disconnectListeners.forEach((listener) =>
          listener({ connectionId: event.connectionId, identifier: event.identifier })
        );
      }
      lastConnected = status.connected;
    });
  };

  return {
    kind: 'star-electron-lan',
    supportedInterfaces: ELECTRON_INTERFACES,

    async describeAvailability(): Promise<PrinterAvailability> {
      return {
        available: true,
        backend: 'star-electron-lan',
        // No StarIO10 SDK on desktop, and none is needed: main speaks Star Line
        // Mode over TCP 9100 itself.
        starSdkIntegrated: false,
      };
    },

    async discoverPrinters(options?: StarDiscoverOptions): Promise<StarDiscoverResult> {
      const result = unwrapBridgeResult(
        'starPrint',
        await bridge.discover({
          timeoutMs: options?.timeoutMs,
          includeCached: options?.includeCached,
          knownAddresses: options?.knownAddresses,
          sweepSubnet: options?.sweepSubnet ?? true,
        })
      );
      return {
        printers: result.printers as StarPrinter[],
        elapsedMs: result.elapsedMs,
        message: result.message,
        interfaceTypes: ['lan'],
        requestedInterfaceTypes: ['lan'],
        ...(result.diagnostics ? { diagnostics: result.diagnostics } : {}),
      };
    },

    async connect(connection: StarPrinterConnection): Promise<StarPrintResult> {
      return unwrapBridgeResult(
        'starPrint',
        await bridge.connect(toLanConnection(connection))
      ) as StarPrintResult;
    },

    async disconnect(target?: StarPrinterTarget): Promise<StarPrintResult> {
      return unwrapBridgeResult(
        'starPrint',
        await bridge.disconnect(toLanTarget(target))
      ) as StarPrintResult;
    },

    async getStatus(target?: StarPrinterTarget): Promise<StarPrinterStatus> {
      return unwrapBridgeResult(
        'starPrint',
        await bridge.getStatus(toLanTarget(target))
      ) as StarPrinterStatus;
    },

    async printReceipt(request: StarPrintReceiptRequest): Promise<StarPrintResult> {
      return unwrapBridgeResult(
        'starPrint',
        await bridge.printReceipt({
          ...toLanTarget(request),
          items: toLanItems(request),
          cutPaper: request.cutPaper,
          openCashDrawer: request.openCashDrawer,
          copies: request.copies,
          jobId: request.jobId,
        })
      ) as StarPrintResult;
    },

    async printImage(request: StarPrintImageRequest): Promise<StarPrintResult> {
      return unwrapBridgeResult(
        'starPrint',
        await bridge.printImage({
          ...toLanTarget(request),
          imageBase64: request.imageBase64,
          alignment: request.alignment,
          width: request.width,
          cutPaper: request.cutPaper,
          openCashDrawer: request.openCashDrawer,
          jobId: request.jobId,
        })
      ) as StarPrintResult;
    },

    async sendRawCommands(request: StarRawCommandRequest): Promise<StarPrintResult> {
      return unwrapBridgeResult(
        'starPrint',
        await bridge.sendRawCommands({
          ...toLanTarget(request),
          commandsBase64: toBase64(request),
          jobId: request.jobId,
        })
      ) as StarPrintResult;
    },

    async openCashDrawer(request?: StarOpenCashDrawerRequest): Promise<StarPrintResult> {
      return unwrapBridgeResult(
        'starPrint',
        await bridge.openCashDrawer({
          ...toLanTarget(request),
          channel: request?.channel,
          pulseOnTimeMs: request?.pulseOnTimeMs,
          pulseOffTimeMs: request?.pulseOffTimeMs,
        })
      ) as StarPrintResult;
    },

    /**
     * Desktop has no Bluetooth peripherals path, and `bluetoothPeripherals` is
     * `false` in the capability row, so nothing on this runtime should ask.
     * `'unknown'` is the honest answer for a backend with no radio — not
     * `'authorized'`, which would invite a caller to try a Bluetooth connect
     * that cannot exist here.
     */
    async getBluetoothAuthorization(): Promise<BluetoothAuthorizationResult> {
      return { status: 'unknown' };
    },

    async addListener<K extends keyof StarPrintEventMap>(
      eventName: K,
      listenerFunc: (event: StarPrintEventMap[K]) => void
    ): Promise<PluginListenerHandle> {
      ensureSubscribed();

      if (eventName === 'printerStatusChanged') {
        const listener = listenerFunc as StatusListener;
        statusListeners.add(listener);
        return {
          remove: async () => {
            statusListeners.delete(listener);
          },
        };
      }
      if (eventName === 'printerDisconnected') {
        const listener = listenerFunc as DisconnectListener;
        disconnectListeners.add(listener);
        return {
          remove: async () => {
            disconnectListeners.delete(listener);
          },
        };
      }

      // Discovery and per-job events have no channel on the LAN surface. Saying
      // so is deliberate: a handle that silently never fires is how a caller
      // ends up waiting forever for an event that was never going to arrive.
      console.warn(
        `[StarPrint/electron] '${String(eventName)}' is not emitted by the desktop LAN backend; ` +
          'this listener will never fire.'
      );
      return { remove: async () => undefined };
    },

    async removeAllListeners(): Promise<void> {
      statusListeners.clear();
      disconnectListeners.clear();
      unsubscribe?.();
      unsubscribe = null;
    },
  };
}
